/**
 * Dashboard Filter & Export Control Bar
 * Allows time-window filtering (7d, 14d, 30d, 90d, all, custom),
 * notes keyword search, and one-click CSV export / print actions.
 */

import React, { useState } from 'react';
import { DateRangePreset } from '@/types/record';
import { Button } from '@/components/common/Button';

export interface FilterBarProps {
  activePreset: DateRangePreset;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
  totalFilteredCount: number;
  totalCount: number;
  onPresetChange: (preset: DateRangePreset) => void;
  onDateRangeChange: (start: string, end: string) => void;
  onSearchChange: (query: string) => void;
  onReset: () => void;
  onExportCSV: () => void;
  onPrint?: () => void;
  className?: string;
}

const PRESET_OPTIONS: { id: DateRangePreset; label: string }[] = [
  { id: '7d', label: '近 7 天' },
  { id: '14d', label: '近 14 天' },
  { id: '30d', label: '近 30 天' },
  { id: '90d', label: '近 90 天' },
  { id: 'all', label: '全部歷史' },
  { id: 'custom', label: '自訂區間' },
];

export const FilterBar: React.FC<FilterBarProps> = ({
  activePreset,
  startDate = '',
  endDate = '',
  searchQuery = '',
  totalFilteredCount,
  totalCount,
  onPresetChange,
  onDateRangeChange,
  onSearchChange,
  onReset,
  onExportCSV,
  onPrint,
  className = '',
}) => {
  const [showCustomInputs, setShowCustomInputs] = useState<boolean>(activePreset === 'custom');
  const [customStart, setCustomStart] = useState<string>(startDate);
  const [customEnd, setCustomEnd] = useState<string>(endDate);

  const handlePresetClick = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setShowCustomInputs(true);
      onPresetChange('custom');
    } else {
      setShowCustomInputs(false);
      onPresetChange(preset);
    }
  };

  const handleApplyCustomDates = (e: React.FormEvent) => {
    e.preventDefault();
    if (customStart && customEnd) {
      onDateRangeChange(customStart, customEnd);
    }
  };

  const isFiltered =
    activePreset !== 'all' ||
    Boolean(searchQuery) ||
    Boolean(startDate) ||
    Boolean(endDate);

  return (
    <div
      className={`
        bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-3.5 sm:p-4 space-y-3
        ${className}
      `}
    >
      {/* Top Row: Presets & Action Buttons */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        {/* Preset Chips */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-serif text-xs font-bold text-ink-700 mr-1 uppercase tracking-wider">
            時間區間:
          </span>
          {PRESET_OPTIONS.map((opt) => {
            const isActive = activePreset === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => handlePresetClick(opt.id)}
                className={`
                  px-2.5 py-1 rounded-xs text-xs font-serif transition-all border
                  ${
                    isActive
                      ? 'bg-ink-900 text-paper-100 border-ink-900 font-bold shadow-paper-sm'
                      : 'bg-paper-200 hover:bg-paper-300 text-ink-800 border-paper-400'
                  }
                `}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Right Actions: Export CSV & Print */}
        <div className="flex items-center gap-2 shrink-0">
          {onPrint && (
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-paper-100 hover:bg-paper-200 border border-paper-400 text-ink-800 text-xs font-serif font-semibold rounded-xs shadow-paper-sm transition-colors"
              title="列印醫生諮詢專用紙本紀錄"
            >
              <span>🖨 列印就診單</span>
            </button>
          )}

          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onExportCSV}
            className="flex items-center gap-1.5"
          >
            <span>📥 匯出 CSV 試算表</span>
          </Button>
        </div>
      </div>

      {/* Middle Row: Custom Date Range Form (collapsible / visible if custom) */}
      {(showCustomInputs || activePreset === 'custom') && (
        <form
          onSubmit={handleApplyCustomDates}
          className="flex flex-wrap items-center gap-2 p-2.5 bg-paper-200/80 border border-dashed border-paper-400 rounded-xs text-xs font-serif"
        >
          <span className="font-bold text-ink-800">自訂起訖：</span>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="bg-white border border-paper-400 rounded-xs px-2 py-1 font-mono text-xs text-ink-900 outline-none focus:border-ink-900"
          />
          <span className="text-ink-500">至</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="bg-white border border-paper-400 rounded-xs px-2 py-1 font-mono text-xs text-ink-900 outline-none focus:border-ink-900"
          />
          <button
            type="submit"
            className="px-3 py-1 bg-ink-900 hover:bg-ink-800 text-paper-100 font-serif font-bold rounded-xs transition-colors"
          >
            套用區間
          </button>
        </form>
      )}

      {/* Bottom Row: Search Notes & Stats summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pt-2 border-t border-paper-300">
        {/* Search Notes Input */}
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="搜尋備註關鍵字（如：晨起、服藥、快走）..."
            className="w-full bg-paper-200 border border-paper-400 rounded-xs pl-8 pr-7 py-1 text-xs text-ink-900 placeholder:text-ink-400 outline-none focus:bg-white focus:border-ink-900 font-serif"
          />
          <span className="absolute left-2.5 top-1.5 text-xs text-ink-400">
            🔍
          </span>
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1 text-xs text-ink-400 hover:text-ink-900"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter State Status & Reset */}
        <div className="flex items-center gap-3 text-xs font-serif text-ink-600">
          <span>
            顯示紀錄: <strong>{totalFilteredCount}</strong> / 全部 <strong>{totalCount}</strong> 筆
          </span>

          {isFiltered && (
            <button
              type="button"
              onClick={onReset}
              className="text-red-800 hover:text-red-950 font-bold underline transition-colors"
            >
              重設篩選
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
